import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  CalendarBlank, Thermometer, Leaf, CheckCircle, ArrowRight,
  Lightbulb, SpinnerGap, Drop, TrendUp
} from '@phosphor-icons/react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { AskMoeButton } from './AskMoeDrawer';
import { API_URL } from '../config/api';

/**
 * ThisWeekModule — the "what should I do today?" answer at the top of the dashboard.
 *
 * Surfaces the top incomplete action from the user's Smart Lawn Plan, plus the
 * weather/GDD context that makes the action relevant right now.
 *
 * Hides gracefully for brand-new users (no plan yet) — the First Steps guide
 * handles them. Reappears the moment they have their first plan.
 */
export const ThisWeekModule = () => {
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completingIdx, setCompletingIdx] = useState(null);
  const [lawnSqft, setLawnSqft] = useState(0);

  const loadData = useCallback(async () => {
    try {
      const [planRes, dashRes, zonesRes] = await Promise.all([
        axios.get(`${API_URL}/api/smart-plan/cached`, { withCredentials: true }),
        axios.get(`${API_URL}/api/dashboard`, { withCredentials: true }).catch(() => null),
        axios.get(`${API_URL}/api/property/zones`, { withCredentials: true }).catch(() => null),
      ]);
      setPlan(planRes.data);
      if (dashRes) setDashboard(dashRes.data);
      if (zonesRes?.data?.length) {
        const total = zonesRes.data.reduce((sum, z) => sum + (parseFloat(z.sqft) || 0), 0);
        setLawnSqft(total);
      }
    } catch {
      // silently fail — module hides
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Hide the module entirely if there's no meaningful plan yet
  if (loading) {
    return (
      <Card className="border-2 border-primary/20" data-testid="this-week-loading">
        <CardContent className="p-5 flex items-center gap-3 text-muted-foreground text-sm">
          <SpinnerGap size={18} className="animate-spin" />
          <span>Checking today's focus…</span>
        </CardContent>
      </Card>
    );
  }

  const actions = plan?.actions || [];
  const completed = plan?.completed_actions || [];

  // Find the first incomplete action
  const nextIdx = actions.findIndex((_, idx) => !completed.includes(idx));
  const nextAction = nextIdx >= 0 ? actions[nextIdx] : null;

  // Brand-new users: no plan yet — don't show this module at all (guide handles them)
  if (!actions.length) return null;

  // User has crushed every action — celebration state
  const allDone = nextIdx === -1;

  const gdd = dashboard?.gdd?.total_ytd;
  const temp = dashboard?.weather?.current?.temperature;
  const nextRain = dashboard?.weather?.daily?.[0]?.precipitation_probability_max;

  const handleMarkDone = async () => {
    if (nextIdx < 0) return;
    setCompletingIdx(nextIdx);
    try {
      await axios.put(
        `${API_URL}/api/smart-plan/complete-action`,
        { action_idx: nextIdx, completed: true },
        { withCredentials: true }
      );
      toast.success('Action logged — nice work.');
      await loadData();
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Could not mark complete';
      toast.error(typeof detail === 'string' ? detail : 'Could not mark complete');
    } finally {
      setCompletingIdx(null);
    }
  };

  const today = new Date().toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric',
  });

  // ─────────── All actions complete — celebration state ───────────
  if (allDone) {
    return (
      <Card className="border-2 border-emerald-500/40 bg-gradient-to-br from-emerald-500/10 via-transparent to-emerald-500/5" data-testid="this-week-done">
        <CardContent className="p-5 sm:p-6">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-lg bg-emerald-500/20 flex items-center justify-center shrink-0">
              <CheckCircle size={22} weight="fill" className="text-emerald-600" />
            </div>
            <div className="flex-1">
              <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-700 dark:text-emerald-400 mb-0.5">
                {today} · This week on your lawn
              </p>
              <h3 className="text-lg font-bold">You're caught up. 🎯</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Every action in your Smart Plan is done. New recommendations appear as your lawn conditions change.
              </p>
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate('/my-plan')}
                className="mt-3 gap-1"
                data-testid="this-week-view-plan"
              >
                Review your full plan <ArrowRight size={14} />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // ─────────── Normal state — surface the next action ───────────
  const priority = nextAction.priority || 'medium';
  const priorityCfg = {
    critical: { label: 'Do this now', color: 'bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/30' },
    high:     { label: 'High priority',  color: 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30' },
    medium:   { label: 'This week',    color: 'bg-primary/15 text-primary border-primary/30' },
    low:      { label: 'When you can', color: 'bg-muted text-muted-foreground border-border' },
  }[priority] || { label: 'Next up', color: 'bg-primary/15 text-primary border-primary/30' };

  return (
    <Card className="border-2 border-primary/30 bg-gradient-to-br from-primary/5 via-transparent to-accent/5 overflow-hidden" data-testid="this-week-module">
      <CardContent className="p-0">
        <div className="p-5 sm:p-6">
          {/* Header row */}
          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
            <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
              <CalendarBlank size={12} weight="fill" />
              {today}
            </div>
            <span className={`text-[10px] px-2 py-1 rounded-full font-bold uppercase tracking-wider border ${priorityCfg.color}`}>
              {priorityCfg.label}
            </span>
          </div>

          {/* The ask */}
          <p className="text-[10px] font-bold uppercase tracking-widest text-primary mb-1">
            This week on your lawn
          </p>
          <h2 className="text-xl sm:text-2xl font-bold leading-tight mb-2" data-testid="this-week-action-title">
            {nextAction.title || nextAction.action}
          </h2>
          {nextAction.why && (
            <p className="text-sm text-muted-foreground leading-relaxed mb-4" data-testid="this-week-action-why">
              {nextAction.why}
            </p>
          )}

          {/* Context strip */}
          {(gdd !== undefined || temp !== undefined) && (
            <div className="flex flex-wrap gap-3 mb-4 text-xs text-muted-foreground">
              {gdd !== undefined && (
                <span className="flex items-center gap-1.5 bg-background/60 rounded-full px-2.5 py-1 border border-border/40">
                  <TrendUp size={12} className="text-emerald-600" />
                  <span className="font-semibold text-foreground">{Math.round(gdd)}</span> GDD YTD
                </span>
              )}
              {temp !== undefined && (
                <span className="flex items-center gap-1.5 bg-background/60 rounded-full px-2.5 py-1 border border-border/40">
                  <Thermometer size={12} className="text-rose-600" />
                  <span className="font-semibold text-foreground">{Math.round(temp)}°F</span> now
                </span>
              )}
              {nextRain !== undefined && (
                <span className="flex items-center gap-1.5 bg-background/60 rounded-full px-2.5 py-1 border border-border/40">
                  <Drop size={12} className="text-sky-600" />
                  <span className="font-semibold text-foreground">{nextRain}%</span> rain today
                </span>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              onClick={handleMarkDone}
              disabled={completingIdx === nextIdx}
              className="gap-1.5"
              data-testid="this-week-mark-done"
            >
              {completingIdx === nextIdx ? (
                <SpinnerGap size={14} className="animate-spin" />
              ) : (
                <CheckCircle size={14} weight="fill" />
              )}
              Mark done
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate('/my-plan')}
              className="gap-1"
              data-testid="this-week-view-plan"
            >
              View full plan <ArrowRight size={14} />
            </Button>
            <div className="ml-auto flex items-center">
              <AskMoeButton
                actionKey={nextAction.category}
                lawnSqft={lawnSqft}
                label="Ask Moe: how do I do this?"
              />
            </div>
          </div>
        </div>

        {/* Footer hint */}
        <div className="px-5 sm:px-6 py-3 border-t border-border/40 bg-background/40 flex items-center gap-2 text-xs text-muted-foreground">
          <Lightbulb size={12} weight="fill" className="text-primary" />
          <span>
            Recommendations auto-refresh with your GDD, weather, and activity.
            {actions.length > 1 && <> {actions.length - completed.length - 1} more queued after this.</>}
          </span>
        </div>
      </CardContent>
    </Card>
  );
};

export default ThisWeekModule;
